import React, { useImperativeHandle, useState, forwardRef } from 'react';

import { FeatureFlag } from 'constants-flags';
import { useFlag } from 'lane-shared/hooks';

import { Icon } from 'design-system-web';
import { datadogRum } from '@datadog/browser-rum';
import cx from 'classnames';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'react-places-autocomplete'. Remove this comment to see the full error message
import XPlacesAutocomplete from 'react-places-autocomplete';
import { Key } from 'ts-key-enum';

import { AddressType } from 'lane-shared/types/AddressType';

import useModalPosition, { ModalPositionEnum } from 'hooks/useModalPosition';

import { buildAddress } from './geocodeAddress';

import styles from './PlacesAutocompleteInline.scss';

export type PlacesAutocompleteHandle = {
  focus: () => void;
  blur: () => void;
  click: () => void;
};

type Props = {
  style?: React.CSSProperties;
  className?: string;
  value: string;
  fieldname?: string;
  label?: string;
  placeholder?: string;
  onChange: (address: AddressType) => void;
  disabled?: boolean;
  testId?: string;
  isRequired?: boolean;
  error?: string;
};

const PlacesAutocompleteV2 = forwardRef<PlacesAutocompleteHandle, Props>(
  (
    {
      style,
      className,
      value,
      fieldname,
      label = 'Address',
      placeholder = 'Search for address',
      onChange,
      disabled,
      testId,
      isRequired,
      error,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [search, setSearch] = useState(value);
    const {
      buttonRef,
      modalRef,
      childrenRef,
      onOpen,
      position,
    } = useModalPosition({
      autoFocus: false,
      align: ModalPositionEnum.Dropdown,
    });

    function clearHandler() {
      setSearch('');
    }

    useImperativeHandle(ref, () => ({
      focus: () => {
        if (childrenRef.current) {
          // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
          childrenRef.current.focus();
        }
      },
      blur: () => {
        if (childrenRef.current) {
          // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
          childrenRef.current.blur();
        }
      },
      click: () => {
        if (childrenRef.current) {
          // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
          childrenRef.current.click();
        }
      },
    }));

    const isMapsModuleFixEnabled = useFlag(FeatureFlag.MapsModuleFixes, false);
    const [geocodeError, setGeocodeError] = useState<string | null>(null);

    async function selected(item: any) {
      setGeocodeError(null);
      if (isMapsModuleFixEnabled) {
        try {
          const address = await buildAddress(item);
          setSearch(item);
          onChange(address);
        } catch (err) {
          datadogRum.addError(err, {
            component: 'PlacesAutocompleteInline',
            action: 'geocode_address',
          });
          setGeocodeError(t('web.components.placesAutocomplete.geocodeError'));
        }
      } else {
        const address = await buildAddress(item);
        setSearch(item);
        onChange(address);
      }
    }

    const isTouched = String(search).length > 0;

    const requiredFieldAsteriskSymbol = '*';

    return (
      <div
        className={cx(styles.PlacesAutocomplete, className)}
        style={style}
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'RefObject<HTMLElement>' is not assignable to... Remove this comment to see the full error message
        ref={buttonRef}
        data-test={testId}
      >
        <label htmlFor={fieldname} className={styles.label}>
          {t(label)}
          {isRequired ? (
            <span className={styles.InputLabelRequiredAstrisk}>
              {requiredFieldAsteriskSymbol}
            </span>
          ) : null}
        </label>
        <XPlacesAutocomplete
          value={search}
          onChange={setSearch}
          onSelect={selected}
        >
          {({ getInputProps, suggestions, getSuggestionItemProps }: any) => (
            <div className={styles.search}>
              <input
                {...getInputProps({
                  placeholder,
                  className: styles.input,
                })}
                name={fieldname}
                label={label}
                aria-label={!label ? fieldname : undefined}
                value={search}
                onClick={onOpen}
                disabled={disabled}
                ref={childrenRef}
                data-touched={isTouched}
                data-test="placeAutocompleteSearch"
                data-error={Boolean(error)}
              />
              {suggestions.length > 0 &&
                ReactDOM.createPortal(
                  <ul
                    className={styles.results}
                    // @ts-expect-error ts-migrate(2322) FIXME: Type 'RefObject<HTMLElement>' is not assignable to... Remove this comment to see the full error message
                    ref={modalRef}
                    // @ts-expect-error ts-migrate(2322) FIXME: Type '{ top: number; left: number; height?: number... Remove this comment to see the full error message
                    style={position}
                    data-test="addressDropdown"
                  >
                    {suggestions.map((suggestion: any) => (
                      <li
                        key={suggestion.placeId || suggestion.description}
                        {...getSuggestionItemProps(suggestion)}
                        data-is-active={suggestion.active}
                      >
                        <span>{suggestion.description}</span>
                      </li>
                    ))}
                  </ul>,
                  document.body
                )}
              <div className={cx(styles.iconWrapper, styles.leftIconWrapper)}>
                <Icon className={styles.icon} name="search" />
              </div>
              {isTouched && !error && (
                <div
                  className={cx(
                    styles.iconWrapper,
                    styles.rightIconWrapper,
                    styles.interactive
                  )}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => e.key === Key.Enter && clearHandler()}
                  onClick={clearHandler}
                >
                  <Icon
                    className={styles.icon}
                    name="times"
                    testId="clearIcon"
                  />
                </div>
              )}
              {error && (
                <div
                  className={cx(styles.iconWrapper, styles.rightIconWrapper)}
                >
                  <Icon
                    className={cx(styles.icon, styles.errorIcon)}
                    name="exclamation-circle"
                    set="FontAwesome"
                    testId="errorIcon"
                  />
                </div>
              )}
            </div>
          )}
        </XPlacesAutocomplete>
        {(error || (isMapsModuleFixEnabled && geocodeError)) && (
          <div className={styles.errorContainer}>
            <p
              className={styles.error}
              data-test="placesAutocompleteInlineErrorMessage"
            >
              {error || (isMapsModuleFixEnabled && geocodeError)}
            </p>
          </div>
        )}
      </div>
    );
  }
);

export default PlacesAutocompleteV2;
